/**
 * ExportCard, sheet 43 callout 9 (mission 10 WP16 T095; FR-017, FR-011's screen half; D.6, A.3; C-013,
 * FR-022; contracts/tenant-lifecycle-api.md "POST /v1/tenant/export").
 *
 * `exportTitle` and `exportBody`, then either the Request export button or, with an open request (the window
 * read's `export_request`, or the answer of a request just made: 202 new, 200 the open one), `exportOpen` with
 * its due date in `<time datetime>` and `exportFulfilment` with `{days}` from `fulfilment_days`, never a
 * literal. The self-service download is v1.x (43/9's second part). A refusal renders its band in the card.
 */
import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ApiError } from '@/lib/api'
import type { ExportRequest } from '@/lib/api/retention'
import { date } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { useExportRequestMutation } from '@/lib/queries'

import { WithDate } from './dates'

export interface ExportCardProps {
  /** The window read's open request, or null. */
  request: ExportRequest | null
  /** `terms.fulfilment_days` from the window read. */
  fulfilmentDays: number
}

export function ExportCard({ request, fulfilmentDays }: ExportCardProps) {
  const mutation = useExportRequestMutation()
  const open = mutation.data?.request ?? request
  const days = mutation.data?.fulfilment_days ?? fulfilmentDays
  const error = mutation.error

  return (
    <Card>
      <CardHeader>
        <CardTitle>{copyText('exportTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-body-sm text-navy-700">{copyText('exportBody')}</p>
        {open !== null ? (
          <>
            <p className="text-body-sm text-navy-950">
              <WithDate
                text={PROVISIONAL_COPY.exportOpen.render({
                  date: date(open.due_at),
                })}
                iso={open.due_at}
              />
            </p>
            <p className="text-body-sm text-navy-700">{PROVISIONAL_COPY.exportFulfilment.render({ days })}</p>
          </>
        ) : (
          <div>
            <Button variant="secondary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {copyText('exportButton')}
            </Button>
          </div>
        )}
        {error === null ? null : error instanceof ApiError ? (
          <ProblemBand problem={error.problem} />
        ) : (
          <Band kind="blocked">
            <p>{copyText('genericError')}</p>
          </Band>
        )}
      </CardContent>
    </Card>
  )
}
